import { WFDialogConnector } from "@multiweb/reactConnectors/WFDialogConnector";
import { ModalWindowType } from "@multiweb/reactCore/bridge";
import { IOWObjectList, IParticipant } from "@multiweb/types/multiweb";
import { PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { invariant } from "../../../../../Global/Base/Utils/AssertUtils";
import { EOWValidateException } from "../../../../../OW.Remoting/Basic/OWExceptions";
import { IOWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import {
    IParticipantBase,
    IWorkflowMenuItem,
} from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { ErrorHandler } from "../../../../Errors";
import { ActionContext, ActionType, IActionContext, MenuType } from "../../../../UI.Controls/Actions/IAction";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { StringBuilder } from "../../DataBuilders/StringBuilder";
import { FormManager } from "../../FormManager/IFormManager";
import { MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { FormResultType } from "../Forms/FormResult";
import { IValidatableMasterableFrame, IWorkflowMasterableFrame } from "../Frames/FrameInterfaces";
import { SharedMethods } from "../SharedMethods";
import { WindowedDetailFrame } from "../WindowedDetailFrame";
import { XGAction } from "./XGAction";

export abstract class WFActionBase extends XGAction {
    public caption: string;

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat doporučené atributy.
     */
    public validateRecommended: boolean;

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat odložené povinné atributy
     */
    public validatePostReq: boolean;

    /**
     * Příznak, zda je možné při spouštění WorkFlow ihned vybírat konkrétní uživatele
     */
    protected _chooseFromUsersDefault: boolean;
    /*
    * Identifikace aktivity z vlastností (wf designer)
    */
    protected _nextActivityID: string;

    protected _participantsSelecting: boolean = false;
    protected _modalWindowConnector: WFDialogConnector;

    public override get context(): ActionContext {
        return ActionContext.objectAction;
    }

    public override get actionType(): ActionType {
        return ActionType.workflow;
    }

    constructor(params: ControlParams) {
        super(params);

        this.toMenu = MenuType.mtWorkflow;
    }

    protected async _finishExecution(wfMenuItem: IWorkflowMenuItem, object: OWObject, refreshOwner: boolean = true, participants?: IParticipantBase[]): Promise<void> {
        if (wfMenuItem.ParamFormName) {
            await FormManager.instance.openFormModal({
                formName: wfMenuItem.ParamFormName,
                parametricData: {
                    //viewMode: true,
                    inLOVMode: false,
                    applyLOVCondition: false,
                    autoSelectFirstRow: false,
                    masterObject: object,
                    masterLinkToAttr: wfMenuItem.LinkAttrForSelObj,
                    setValues: wfMenuItem.SetValues,
                    openInEdit: false,
                    ownerForm: this.owner ? this.owner.ownerForm : undefined,
                    executerFrame: this.owner,
                    purposeType: MenuType.mtWorkflow,
                    returnMainInstance: true,
                    formContext: this.owner.frameContext,
                    transactionContextGetter: this._useMasterContext ? this.owner.frameContext.getTransactionContextId : null,
                },
                // onClose
                onClose: async (_sender, result) => {
                    if (result?.resultType === FormResultType.ok) {
                        invariant(result.resultValue instanceof IOWObject, "Parametric form didn't return selected instance");
                        await this._executeWfAction(object, wfMenuItem, refreshOwner, result.resultValue, participants);
                    }
                },
                type: ModalWindowType.parametricForm,
                showBreadcrumbNav: !!this.owner,
            });
        } else {
            await this._executeWfAction(object, wfMenuItem, refreshOwner, undefined, participants);
        }
    }

    /**
     * Spusteni WF akce
     */
    protected async _executeWfAction(object: IOWObject, wfMenuItem: IWorkflowMenuItem, refreshOwner: boolean = true, paramFormObject?: IOWObject, participants?: IParticipantBase[]): Promise<void> {
        if (!object)
            throw new Error("Argument 'object' can't be null");

        try {
            UIManager.instance.showLoader();

            const result = await OWManager.objectWFActionExecute(object.instanceID, wfMenuItem.ID, participants, paramFormObject?.instanceID ?? undefined, this._getRequestContext());
            paramFormObject?.dispose();

            //Pokud během obj.WFActionExecute(...) nastal EOWLongTransactionTimeOut (metoda byla pouštěna v režimu dlouhé transakci a jeji práce se neukončila v zadaném termínu)
            //metoda vrací false, a zobrazuje se informační okno
            if (result.isInLongTransaction) {
                MessageWindow.showAlert(new StringBuilder("LongTransaction"));
                return null;
            }

            if (refreshOwner)
                if (this.owner) {
                    if (this.owner instanceof IWorkflowMasterableFrame || this.owner instanceof WindowedDetailFrame) {
                        this.owner.reloadWFItems(); // po provedení akce se znovu načte WF, protože mohlo dojít ke změně jeho stavu
                    }

                    await this.owner.refreshActualObject(); // Refresh je třeba v případě existujícího ownera spustit takto, aby se aktualizovaly i případné podřízené objekty
                } else {
                    await object.refresh();
                }

            UIManager.instance.showResultMessages(result.messages);
        } catch (error) {
            if (error instanceof EOWValidateException && this.owner instanceof IValidatableMasterableFrame) {
                await SharedMethods.handleValidationErrors([object as OWObject], error, this.owner);
            } else {
                ErrorHandler.instance.handleError(error);
            }
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Vyrenderuje skrz React modál pro výběr hráčů.
     * Po potvrzení výběru a validace se okno zavře.
     *
     * @param instanceID Identifikace objektu.
     * @param activityID Identifikace aktivity.
     * @param chooseFromUsersDefault Zda se ihned zobrazí uživatelé rolí.
     */
    protected async _handleParticipantsWindow(instanceID: string, objectList: IOWObjectList, activityID: string, chooseFromUsersDefault: boolean): Promise<IParticipantBase[]> {
        UIManager.instance.hideLoader();
        const deferred = PromiseUtils.defer<IParticipant[]>();
        this._modalWindowConnector = new WFDialogConnector(deferred, instanceID, objectList, activityID, chooseFromUsersDefault);
        this._modalWindowConnector.initializeVisual();

        let data: IParticipant[] = [];

        try {
            data = await deferred.promise;
        } finally {
            this._modalWindowConnector.dispose();
        }

        return data;
    }

    /**
     *
     * @param id V případě, že je akce spouštěna přes ID objektu místo instance
     * @param className V případě, že je akce spouštěna přes ID objektu místo instance
     */
    protected abstract override _internalExecute(context?: IActionContext): Promise<void>;
}
